
import { useState, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import { BirdieGameGroup } from "@/types";
import EmptyGamesView from "./EmptyGamesView";
import LoadingGamesView from "./LoadingGamesView";
import GamesList from "./GamesList";
import { useBirdieRecords } from "./useBirdieRecords";

const PAGE_SIZE = 3;

interface ActiveGamesProps {
  userGames: BirdieGameGroup[];
  isLoading: boolean;
  currentUserId?: string;
}

// Memoized component to prevent unnecessary re-renders
const ActiveGames = memo(({ userGames, isLoading, currentUserId }: ActiveGamesProps) => {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Get birdie records for these games
  const { birdieRecords, isLoadingBirdies } = useBirdieRecords();

  // Reset visible count when game list changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [userGames]);

  // Build a map of gameId -> most recent birdie date for sorting
  const latestBirdieByGame = new Map<string, string>();
  for (const record of birdieRecords) {
    for (const gameId of record.gameIds) {
      const existing = latestBirdieByGame.get(gameId);
      if (!existing || record.date > existing) {
        latestBirdieByGame.set(gameId, record.date);
      }
    }
  }

  // Filter and sort: non-archived, Lone Wolf first, then by most recent birdie descending
  const sortedGames = userGames
    .filter(game => !game.isArchived)
    .sort((a, b) => {
      // Sample game always last
      if (a.isSample && !b.isSample) return 1;
      if (!a.isSample && b.isSample) return -1;
      // Lone Wolf always first
      if (a.isLoneWolf && !b.isLoneWolf) return -1;
      if (!a.isLoneWolf && b.isLoneWolf) return 1;
      const dateA = latestBirdieByGame.get(a.id) || '';
      const dateB = latestBirdieByGame.get(b.id) || '';
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      return b.year - a.year;
    });

  const visibleGames = sortedGames.slice(0, visibleCount);
  const hasMore = visibleCount < sortedGames.length;

  const handleViewGame = (gameId: string) => {
    navigate(`/games/${gameId}`);
  };

  const handleViewAllGames = () => {
    navigate("/games");
  };

  const handleShowMore = () => {
    setVisibleCount(prev => prev + PAGE_SIZE);
  };

  return (
    <div className="px-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-forest">Active Games</h2>
        <button
          className="text-sm text-sage hover:text-forest transition-colors font-medium"
          onClick={handleViewAllGames}
        >
          View All
        </button>
      </div>

      {isLoading || isLoadingBirdies ? (
        <LoadingGamesView />
      ) : visibleGames.length > 0 ? (
        <>
          <GamesList
            activeGames={visibleGames}
            birdieRecords={birdieRecords}
            onViewGame={handleViewGame}
            currentUserId={currentUserId}
          />
          {hasMore && (
            <button
              className="w-full mt-3 py-2 text-sm text-sage hover:text-forest transition-colors font-medium"
              onClick={handleShowMore}
            >
              Show More
            </button>
          )}
        </>
      ) : (
        <EmptyGamesView />
      )}
    </div>
  );
});

// Add display name for debugging
ActiveGames.displayName = "ActiveGames";

export default ActiveGames;
